import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import {
  MEDIA_LIMITS, VIDEO_BUCKET, currentAccessToken, mediaPath, privateMarker,
  probeAudioDuration, probeVideo, publicMediaUrl, uploadToStorage, validateMediaFile,
  type MediaKind,
} from "@/lib/media";

export type AttachedMedia = {
  /** Local key while composing. */
  key: string;
  kind: MediaKind;
  /** Public URL, or a `secure://` marker for privately stored video. */
  url: string;
  /** Object URL for local preview only — never persisted. */
  previewUrl?: string;
  posterUrl?: string | null;
  durationSeconds?: number | null;
  byteSize?: number | null;
  storageBucket?: string | null;
  storagePath?: string | null;
  posterPath?: string | null;
  name: string;
};

export type UploadTask = {
  key: string;
  name: string;
  kind: MediaKind;
  percent: number;
  cancel: () => void;
};

/**
 * One upload pipeline for photos, video, audio and documents.
 *
 * Photos, audio and documents keep going to the existing public media bucket.
 * Video goes to the private video store and is only ever played back through a
 * short-lived link, so a raw file URL is never guessable.
 */
export function useMediaUploads(prefix: string, opts?: { maxItems?: number }) {
  const [items, setItems] = useState<AttachedMedia[]>([]);
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const counter = useRef(0);

  const remove = useCallback((key: string) => {
    setItems((prev) => {
      const gone = prev.find((p) => p.key === key);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((p) => p.key !== key);
    });
  }, []);

  const addFiles = useCallback(async (files: File[], userId: string) => {
    if (files.length === 0) return;
    const token = await currentAccessToken().catch((e: Error) => { toast.error(e.message); return null; });
    if (!token) return;

    for (const file of files) {
      if (opts?.maxItems && items.length + 1 > opts.maxItems) {
        toast.error(`You can attach up to ${opts.maxItems} items.`);
        break;
      }
      const kind: MediaKind = file.type.startsWith("video/")
        ? "video"
        : file.type.startsWith("image/")
          ? "image"
          : file.type.startsWith("audio/")
            ? "audio"
            : "document";

      let duration: number | undefined;
      let posterBlob: Blob | null = null;
      if (kind === "video") {
        try {
          const probe = await probeVideo(file);
          duration = probe.duration;
          posterBlob = probe.posterBlob;
        } catch (e: any) {
          toast.error(e?.message ?? "That video could not be read.");
          continue;
        }
      } else if (kind === "audio") {
        duration = await probeAudioDuration(file);
      }

      const problem = validateMediaFile(file, kind, duration);
      if (problem) { toast.error(problem); continue; }

      const key = `m${++counter.current}`;
      const bucket = kind === "video" ? VIDEO_BUCKET : "post-media";
      const path = mediaPath(userId, file, prefix);
      const handle = uploadToStorage(bucket, path, file, token, (percent) =>
        setTasks((prev) => prev.map((t) => (t.key === key ? { ...t, percent } : t))),
      );
      setTasks((prev) => [...prev, { key, name: file.name, kind, percent: 0, cancel: handle.cancel }]);

      try {
        await handle.promise;
      } catch (e: any) {
        setTasks((prev) => prev.filter((t) => t.key !== key));
        if (e?.message !== "Upload cancelled.") toast.error(e?.message ?? "The upload failed.");
        else toast.info("Upload cancelled.");
        continue;
      }

      // Poster frame for the feed thumbnail: a single low-resolution still.
      let posterUrl: string | null = null;
      let posterPath: string | null = null;
      if (posterBlob) {
        const pPath = `${userId}/${prefix}/poster-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
        try {
          await uploadToStorage("post-media", pPath, posterBlob, token).promise;
          posterUrl = publicMediaUrl(pPath);
        } catch { /* a missing poster is not worth failing the upload over */ }
      }

      setTasks((prev) => prev.filter((t) => t.key !== key));
      setItems((prev) => [...prev, {
        key,
        kind,
        url: kind === "video" ? privateMarker(path) : publicMediaUrl(path),
        previewUrl: kind === "video" || kind === "audio" ? URL.createObjectURL(file) : undefined,
        posterUrl,
        durationSeconds: duration ? Math.round(duration) : null,
        byteSize: file.size,
        storageBucket: bucket,
        storagePath: path,
        posterPath,
        name: file.name,
      }]);
    }
  }, [items.length, opts?.maxItems, prefix]);

  /** Move an attachment one slot earlier or later — publish order follows this list. */
  const move = useCallback((key: string, dir: -1 | 1) => {
    setItems((prev) => {
      const i = prev.findIndex((p) => p.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(i, 1);
      next.splice(j, 0, item!);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setItems((prev) => { prev.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl)); return []; });
    setTasks([]);
  }, []);

  return { items, tasks, addFiles, remove, move, clear, busy: tasks.length > 0, limits: MEDIA_LIMITS };
}

